import { useState, useCallback, useEffect } from 'react';

// <purpose>Selection-mode state for multi-select on a ticket list, plus WHICH verb the selection is
// being made for.</purpose>
// <constraints>There are two levers that both enter selection mode — archive and form-to-excel — and
// they are mutually exclusive by construction: `purpose` holds one or neither, never both. The single
// action button reads `purpose` to decide what it does, so a page never needs a second parallel
// boolean, and the two levers cannot get out of step with each other.
// Switching lever KEEPS the selection: the tickets a clerk picked are the same tickets either way, only
// the verb changes. Leaving selection mode entirely CLEARS it — a selection that outlives its own mode
// is invisible state that the next action would silently consume.</constraints>
export type SelectionPurpose = 'archive' | 'excel';

export function useSelectionMode() {
  const [purpose, setPurposeState] = useState<SelectionPurpose | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Invariant, enforced here rather than at every call site: no selection without a mode to spend it in.
  useEffect(() => { if (purpose === null) setSelected(new Set()); }, [purpose]);

  // Pressing the active lever again turns selection OFF; pressing the other one switches verb.
  const setPurpose = useCallback((p: SelectionPurpose) => {
    setPurposeState(cur => (cur === p ? null : p));
  }, []);

  const toggle = useCallback((uuid: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const exitMode = useCallback(() => setPurposeState(null), []);

  return { mode: purpose !== null, purpose, setPurpose, selected, toggle, clear, exitMode };
}
